/**
 * One elements-config payload per config URL, shared by every widget mount in
 * the document.
 *
 * WHY (CON-21, the anchor case). A hosted page renders the widget TWICE: once
 * in the desktop rail and once inside the mobile drawer. Both are always in the
 * React tree — the desktop rail is hidden with `hidden lg:hidden`, which is CSS,
 * not unmounting — so on a phone the rail's widget fetches the configuration at
 * page load while the drawer's copy is not mounted at all. Tapping Donate then
 * mounted a second widget that asked the network for a payload the page already
 * had, and showed a placeholder for as long as that second round trip took.
 * Measured on the hosted page at 375px with a 1.5s config response: the
 * placeholder was on screen for 1478ms of the drawer's first 1.5 seconds.
 *
 * A SEED IS THE FIRST FRAME, NOT A SUBSTITUTE FOR THE READ. A mount that opens
 * on a seed renders the real, interactive form immediately instead of a
 * placeholder — and then reads the network anyway, applying the result only if
 * it differs (`EmbedForm.jsx`, the `openedOnSeed` guard). CON-21 shipped the
 * opposite first, and this comment described it: the seed replaced the read.
 * That was reversed in `e18dd8579` ("a seeded mount must re-read after all")
 * because skipping the read is what broke the desktop overlay's money path —
 * a mount with a null `configUrl` compared `null === null` true, decided it had
 * opened on a seed, and never ran the one call that sets its payload.
 *
 * What the re-read has to be careful about is object identity, not the request:
 * `setData` handing a form a NEW object while the donor is using it would make
 * every form re-initialise from `props.data` — `StandardForm`'s effect resets
 * the amount, the frequency, the fund and every custom-input answer, and
 * mutates the payload in place while it does. So the read applies only on a
 * difference. The refresh a donation triggers (`RESET_ELEMENT` ->
 * `fetchElementsConfig`) is a direct call and is untouched.
 *
 * WHY THAT IS SAFE, which is the question to ask of it. A seed is only ever
 * visible inside ONE document, and inside that document it shows exactly the
 * payload another mounted widget is already displaying — the hosted page's
 * desktop rail holds its configuration for the whole session and revalidates
 * nothing. So the drawer cannot show a donor anything staler than what is
 * already on the screen beside them. A fresh document starts with a fresh
 * module and therefore an empty store.
 *
 * EVERY CROSSING IS A COPY. The forms mutate the payload they are given —
 * `StandardForm` writes `input.answers` and normalises the amount arrays onto
 * the object — so handing two mounts the same object would let one corrupt the
 * other's configuration. The store clones on the way in and on the way out. A
 * JSON round trip is faithful here because the payload IS parsed JSON.
 *
 * Module scope, not a React context: the two widgets on a hosted page have no
 * common provider, and adding one would change the public shape of a component
 * thirteen call sites mount. The store is keyed by url and a document only ever
 * holds a handful of them, so it is not a growth risk.
 *
 * THE SECOND JOB (CON-75): in-flight dedupe. The seed store above only helps a
 * mount that arrives AFTER another has already resolved. Two mounts that start
 * together — which is what the hosted page does, because both layout branches
 * are always in the React tree — each fire their own request for the same url.
 * `FundraisingBar` and `SupportersList` did exactly that, twice each.
 * `fetchElementsConfigOnce(url)` hands concurrent callers the same promise, so
 * they cost one request between them.
 *
 * Deliberately NOT wired into `EmbedForm`: its read is a revalidation with
 * different semantics (see above), and collapsing it into a shared promise
 * would make a later mount adopt an earlier mount's in-flight answer, which is
 * the staleness the re-read exists to avoid.
 */

/** url -> the last payload the backend returned for it */
const resolvedConfigs = new Map();

function clone(payload) {
  return JSON.parse(JSON.stringify(payload));
}

/**
 * A copy of the payload a previous mount resolved for `url`, or null.
 *
 * @param {string|null} url
 * @returns {object|null}
 */
export function readElementsConfig(url) {
  if (!url) return null;
  const stored = resolvedConfigs.get(url);
  return stored ? clone(stored) : null;
}

/**
 * Record a successful read so later mounts can seed from it.
 *
 * @param {string|null} url
 * @param {object} data
 */
export function rememberElementsConfig(url, data) {
  if (!url || !data) return;
  resolvedConfigs.set(url, clone(data));
}

/** Test seam. Never called by application code. */
export function __resetElementsConfigCache() {
  resolvedConfigs.clear();
}

/**
 * url -> the in-flight request for it.
 *
 * Only ever holds PENDING promises: an entry is deleted the moment its request
 * settles, success or failure. The resolved payload lives in the store above,
 * where the seed API already governs its lifetime; a failure is not cached at
 * all, so a retry is a real retry.
 */
const inFlightRequests = new Map();

/**
 * Fetch an elements-config url, sharing one request between concurrent callers.
 *
 * @param {string} url
 * @param {(url: string) => Promise<Response>} [fetchImpl] test seam
 * @returns {Promise<Response>} the SAME Response object for concurrent callers,
 *   so a caller that needs the body must clone it — `readBodyOnce` below does.
 */
export function fetchElementsConfigOnce(url, fetchImpl = fetch) {
  const pending = inFlightRequests.get(url);
  if (pending) return pending;

  // `fetchImpl(url)` is called SYNCHRONOUSLY, not from a `.then`: deferring it
  // by a microtask would delay the very request this function exists to get on
  // the wire sooner. The try/catch is for an implementation that throws rather
  // than rejecting.
  let started;
  try {
    started = Promise.resolve(fetchImpl(url));
  } catch (error) {
    return Promise.reject(error);
  }

  const request = started
    .finally(() => {
      // Before the caller's own `.then` runs, so a caller that immediately
      // re-requests the same url after this one settles gets a fresh request
      // rather than a resolved promise it cannot read twice.
      inFlightRequests.delete(url);
    });

  inFlightRequests.set(url, request);
  return request;
}

/**
 * A `Response` body can only be read once, and `fetchElementsConfigOnce` hands
 * the same object to every concurrent caller. Clone before reading.
 *
 * @param {Response} response
 * @returns {Promise<object>}
 */
export function readSharedJson(response) {
  return response.clone().json();
}

/** Test seam. Never called by application code. */
export function __resetElementsConfigRequests() {
  inFlightRequests.clear();
}
